import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DeviceTable from './DeviceTable';
import FirmwareUpdateModal from './FirmwareUpdateModal';
import DeviceAlertModal from './DeviceAlertModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faClipboardList, faCopy, faKey, faMicrochip, faMobileScreenButton, faPlus, faShuffle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Device, PhoneDevice, User } from '../types';
import { API_BASE_URL } from '../config/api';
import { TESTER_OPTIONS, TesterLevel, resolveTester, testerBadgeClass, testerLabel } from '../utils/tester';
import { roleBadgeClass } from '../utils/roleBadge';
import {
  DEFAULT_LOGGING_MODE,
  getLoggingModeErrorMessage,
  LOGGING_MODE_OPTIONS,
  loggingModeBadgeStyle,
  loggingModeLabel,
  loggingModeShortLabel,
  updateDeviceLoggingMode,
} from '../utils/loggingMode';
import { fieldsForOtaMode, OtaMode } from '../utils/otaMode';

interface UserDetailProps {
  currentUser?: User;
  profileMode?: boolean;
}

interface UserSurveyResult {
  _id: string;
  survey?: {
    _id: string;
    name: string;
  };
  deviceId?: string;
  status: 'completed' | 'opted_out';
  completedAt?: string | Date;
  createdAt?: string | Date;
  questionResponses?: Array<{
    questionId: string;
    prompt: string;
    value: string;
    additionalInfo?: string;
  }>;
}

const UserDetail: React.FC<UserDetailProps> = ({ currentUser, profileMode = false }) => {
  const { user_id: routeUserId } = useParams<{ user_id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = profileMode ? currentUser?._id : routeUserId;

  const [user, setUser] = useState<User | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [surveyResults, setSurveyResults] = useState<UserSurveyResult[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [devicesLoading, setDevicesLoading] = useState<boolean>(true);
  const [surveyResultsLoading, setSurveyResultsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [surveyResultsError, setSurveyResultsError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<'id' | 'email' | 'password' | null>(null);
  const [updatingRole, setUpdatingRole] = useState<boolean>(false);
  const [updatingTester, setUpdatingTester] = useState<boolean>(false);
  const [updatingLoggingMode, setUpdatingLoggingMode] = useState<boolean>(false);
  const [updatingMqttRemoteControl, setUpdatingMqttRemoteControl] = useState<boolean>(false);
  const [deviceLoggingPrompt, setDeviceLoggingPrompt] = useState<number | null>(null);
  const [applyingDeviceLogging, setApplyingDeviceLogging] = useState<boolean>(false);
  const [sendLoggingCommand, setSendLoggingCommand] = useState<boolean>(true);
  const [deletingUser, setDeletingUser] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [updatingPassword, setUpdatingPassword] = useState<boolean>(false);
  const [phoneDeviceName, setPhoneDeviceName] = useState('');
  const [phoneDevicePlatform, setPhoneDevicePlatform] = useState<PhoneDevice['platform']>('ios');
  const [updatingPhoneDevices, setUpdatingPhoneDevices] = useState(false);

  const [updatingOTA, setUpdatingOTA] = useState<string | null>(null);
  const [firmwareUpdateDevice, setFirmwareUpdateDevice] = useState<Device | null>(null);
  const [alertDevice, setAlertDevice] = useState<Device | null>(null);
  const isAdmin = currentUser?.role === 'admin';
  const canManageUser = isAdmin && !profileMode;
  const canToggleMqttRemoteControl = !profileMode && ['admin', 'developer', 'support', 'technician'].includes(currentUser?.role || '');
  const canInstallFirmware = !profileMode && ['admin', 'developer'].includes(currentUser?.role || '');
  const from = searchParams.get('from');
  const backToUsersPath = from === 'internal-testers'
    ? '/deploy/internal/users'
    : from === 'beta-testers'
      ? '/deploy/beta/users'
      : '/users';

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    if (profileMode && currentUser) {
      setUser(currentUser);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/users/id/${userId}`, {
        withCredentials: true,
      });
      setUser(response.data.user);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching user:', err);
      setError(err.response?.data?.message || 'Failed to fetch user');
    } finally {
      setLoading(false);
    }
  }, [currentUser, profileMode, userId]);

  const fetchDevices = useCallback(async () => {
    if (!userId) return;
    setDevicesLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/devices/by-user/${userId}`, {
        withCredentials: true,
      });
      setDevices(response.data.devices);
      setDevicesError(null);
    } catch (err: any) {
      console.error('Error fetching user devices:', err);
      setDevicesError(err.response?.data?.message || 'Failed to fetch devices');
    } finally {
      setDevicesLoading(false);
    }
  }, [userId]);

  const fetchSurveyResults = useCallback(async () => {
    if (!userId || profileMode) {
      setSurveyResults([]);
      setSurveyResultsLoading(false);
      return;
    }
    setSurveyResultsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/surveys/user/${userId}/results`, {
        withCredentials: true,
      });
      setSurveyResults(response.data.results || []);
      setSurveyResultsError(null);
    } catch (err: any) {
      console.error('Error fetching user survey results:', err);
      setSurveyResultsError(err.response?.data?.message || 'Failed to fetch survey results');
    } finally {
      setSurveyResultsLoading(false);
    }
  }, [profileMode, userId]);

  useEffect(() => {
    fetchUser();
    fetchDevices();
    fetchSurveyResults();
  }, [fetchUser, fetchDevices, fetchSurveyResults]);

  const handleCopy = (text: string, field: 'id' | 'email' | 'password') => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const generatePassword = () => {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%&*';
    const randomValues = new Uint32Array(16);
    window.crypto.getRandomValues(randomValues);
    const generated = Array.from(randomValues, (value) => characters[value % characters.length]).join('');
    setNewPassword(generated);
    setActionError(null);
    setActionMessage(null);
  };

  const handleRoleChange = async (role: User['role']) => {
    if (!user || role === user.role) return;

    setUpdatingRole(true);
    setActionError(null);
    setActionMessage(null);

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/users/${encodeURIComponent(user.email)}`,
        { role },
        { withCredentials: true }
      );
      setUser({
        ...user,
        ...response.data.user,
      });
      setActionMessage('User type updated.');
    } catch (err: any) {
      console.error('Error updating user type:', err);
      setActionError(err.response?.data?.message || 'Failed to update user type');
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleLoggingModeChange = async (mode: number) => {
    if (!user || (user.loggingMode ?? DEFAULT_LOGGING_MODE) === mode) return;

    setUpdatingLoggingMode(true);
    setActionError(null);
    setActionMessage(null);

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/users/${encodeURIComponent(user.email)}`,
        { loggingMode: mode },
        { withCredentials: true }
      );
      setUser({
        ...user,
        ...response.data.user,
      });
      setActionMessage('Logging mode updated.');
      if (devices.length > 0) {
        setDeviceLoggingPrompt(mode);
      }
    } catch (err: any) {
      console.error('Error updating logging mode:', err);
      setActionError(err.response?.data?.message || 'Failed to update logging mode');
    } finally {
      setUpdatingLoggingMode(false);
    }
  };

  const applyLoggingModeToDevices = async (mode: number) => {
    setApplyingDeviceLogging(true);
    setActionError(null);
    try {
      await Promise.all(
        devices.map((device) =>
          sendLoggingCommand
            ? updateDeviceLoggingMode(API_BASE_URL, device.serial, mode)
            : axios.patch(
                `${API_BASE_URL}/devices/${device.serial}`,
                { loggingMode: mode },
                { withCredentials: true }
              )
        )
      );

      setDevices((prev) => prev.map((device) => ({ ...device, loggingMode: mode })));
      setDevicesError(null);
      const deviceCount = `${devices.length} device${devices.length === 1 ? '' : 's'}`;
      setActionMessage(
        sendLoggingCommand
          ? `Logging mode updated. ${deviceCount} set to "${loggingModeLabel(mode)}" and notified over MQTT.`
          : `Logging mode updated. ${deviceCount} set to "${loggingModeLabel(mode)}".`
      );
    } catch (err: any) {
      console.error('Error updating devices logging mode:', err);
      setActionError(err.response?.data?.message || "Failed to update this user's devices");
    } finally {
      setApplyingDeviceLogging(false);
      setDeviceLoggingPrompt(null);
    }
  };

  const handleMqttRemoteControlChange = async (enabled: boolean) => {
    if (!user || Boolean(user.allowMqttRemoteControl) === enabled) return;

    setUpdatingMqttRemoteControl(true);
    setActionError(null);
    setActionMessage(null);

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/users/${encodeURIComponent(user.email)}`,
        { allowMqttRemoteControl: enabled },
        { withCredentials: true }
      );
      setUser({
        ...user,
        ...response.data.user,
      });
      setActionMessage(enabled
        ? 'MQTT remote control enabled.'
        : 'MQTT remote control disabled.');
    } catch (err: any) {
      console.error('Error updating MQTT remote control:', err);
      setActionError(err.response?.data?.message || 'Failed to update MQTT remote control');
    } finally {
      setUpdatingMqttRemoteControl(false);
    }
  };

  const handleTesterChange = async (tester: TesterLevel) => {
    if (!user || resolveTester(user) === tester) return;

    setUpdatingTester(true);
    setActionError(null);
    setActionMessage(null);

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/users/${encodeURIComponent(user.email)}`,
        { tester },
        { withCredentials: true }
      );
      setUser({
        ...user,
        ...response.data.user,
      });
      setActionMessage('Tester updated.');
    } catch (err: any) {
      console.error('Error updating tester:', err);
      setActionError(err.response?.data?.message || 'Failed to update tester');
    } finally {
      setUpdatingTester(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!user) return;
    const confirmed = window.confirm(`Delete ${user.firstName} ${user.lastName} (${user.email})? This cannot be undone.`);
    if (!confirmed) return;

    setDeletingUser(true);
    setActionError(null);
    setActionMessage(null);

    try {
      await axios.delete(`${API_BASE_URL}/users/${encodeURIComponent(user.email)}`, {
        withCredentials: true,
      });
      navigate(backToUsersPath);
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setActionError(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (!user || !newPassword) return;

    setUpdatingPassword(true);
    setActionError(null);
    setActionMessage(null);

    try {
      await axios.patch(
        `${API_BASE_URL}/users/${encodeURIComponent(user.email)}/password`,
        { password: newPassword },
        { withCredentials: true }
      );
      setActionMessage('Password updated.');
    } catch (err: any) {
      console.error('Error updating password:', err);
      setActionError(err.response?.data?.message || 'Failed to update password');
    } finally {
      setUpdatingPassword(false);
    }
  };

  const savePhoneDevices = async (phoneDevices: PhoneDevice[], successMessage: string) => {
    if (!user) return;
    setUpdatingPhoneDevices(true);
    setActionError(null);
    setActionMessage(null);
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/users/${encodeURIComponent(user.email)}`,
        { phoneDevices },
        { withCredentials: true }
      );
      setUser({ ...user, ...response.data.user });
      setActionMessage(successMessage);
    } catch (err: any) {
      console.error('Error updating phone devices:', err);
      setActionError(err.response?.data?.message || 'Failed to update phone devices');
    } finally {
      setUpdatingPhoneDevices(false);
    }
  };

  const handleAddPhoneDevice = async () => {
    if (!user || !phoneDeviceName.trim()) return;
    if (profileMode) {
      setUpdatingPhoneDevices(true);
      setActionError(null);
      setActionMessage(null);
      try {
        const response = await axios.post(
          `${API_BASE_URL}/auth/me/phone-devices`,
          { name: phoneDeviceName.trim(), platform: phoneDevicePlatform },
          { withCredentials: true }
        );
        setUser({ ...user, ...response.data.user });
        setActionMessage('Phone device added.');
        setPhoneDeviceName('');
        setPhoneDevicePlatform('ios');
      } catch (err: any) {
        console.error('Error adding phone device:', err);
        setActionError(err.response?.data?.message || 'Failed to add phone device');
      } finally {
        setUpdatingPhoneDevices(false);
      }
      return;
    }
    const next = [
      ...(user.phoneDevices || []),
      { name: phoneDeviceName.trim(), platform: phoneDevicePlatform },
    ];
    await savePhoneDevices(next, 'Phone device added.');
    setPhoneDeviceName('');
    setPhoneDevicePlatform('ios');
  };

  const handleRemovePhoneDevice = async (deviceId?: string) => {
    if (!user || !deviceId) return;
    if (profileMode) {
      setUpdatingPhoneDevices(true);
      setActionError(null);
      setActionMessage(null);
      try {
        const response = await axios.delete(`${API_BASE_URL}/auth/me/phone-devices/${deviceId}`, {
          withCredentials: true,
        });
        setUser({ ...user, ...response.data.user });
        setActionMessage('Phone device removed.');
      } catch (err: any) {
        console.error('Error removing phone device:', err);
        setActionError(err.response?.data?.message || 'Failed to remove phone device');
      } finally {
        setUpdatingPhoneDevices(false);
      }
      return;
    }
    await savePhoneDevices(
      (user.phoneDevices || []).filter((device) => device._id !== deviceId),
      'Phone device removed.'
    );
  };

  const handleToggleMarked = async (serial: string, value: OtaMode) => {
    try {
      setUpdatingOTA(serial);
      await axios.patch(
        `${API_BASE_URL}/devices/${serial}`,
        { deployment: { otaMode: value } },
        { withCredentials: true }
      );
      setDevices((prev) =>
        prev.map((device) =>
          device.serial === serial
            ? { ...device, deployment: { ...device.deployment, ...fieldsForOtaMode(value) } }
            : device
        )
      );
      setDevicesError(null);
    } catch (err: any) {
      console.error('Error updating device OTA status:', err);
      setDevicesError(`Failed to update OTA status: ${err.response?.data?.message || err.message}`);
      fetchDevices();
    } finally {
      setUpdatingOTA(null);
    }
  };

  const handleUpdateLoggingMode = async (serial: string, mode: number) => {
    try {
      const { device } = await updateDeviceLoggingMode(API_BASE_URL, serial, mode);

      setDevices((prev) =>
        prev.map((entry) =>
          entry.serial === serial ? { ...entry, ...device, loggingMode: mode } : entry
        )
      );
      setDevicesError(null);
    } catch (err: unknown) {
      console.error('Error updating device logging mode:', err);
      setDevicesError(`Failed to update logging mode: ${getLoggingModeErrorMessage(err)}`);
      fetchDevices();
    }
  };

  const formatSurveyResultDate = (value?: string | Date) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const surveyResultStatusLabel = (status: UserSurveyResult['status']) => (
    status === 'opted_out' ? 'Opted Out' : 'Completed'
  );

  const surveyAnswerLines = (result: UserSurveyResult) => {
    if (result.status === 'opted_out') return ['Opted out'];
    const responses = result.questionResponses || [];
    if (!responses.length) return ['No answers recorded'];
    return responses.map((answer) => {
      const value = answer.value || '—';
      const additional = answer.additionalInfo ? ` (${answer.additionalInfo})` : '';
      return `${answer.prompt || 'Question'}: ${value}${additional}`;
    });
  };

  const mqttRemoteControlToggle = user ? (
    <div className={canManageUser ? 'mt-3' : undefined}>
      <div className="fw-bold text-muted small text-uppercase">Allow MQTT Remote Control</div>
      <div className="form-check form-switch mt-1">
        <input
          id="user-mqtt-remote-control"
          className="form-check-input"
          type="checkbox"
          role="switch"
          checked={Boolean(user.allowMqttRemoteControl)}
          disabled={updatingMqttRemoteControl}
          onChange={(e) => handleMqttRemoteControlChange(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="user-mqtt-remote-control">
          {user.allowMqttRemoteControl ? 'Enabled' : 'Disabled'}
        </label>
      </div>
      <div className="form-text">
        Included in the app profile on each launch. When on, the phone may use MQTT remote control.
      </div>
    </div>
  ) : null;

  return (
    <div className="container-fluid mt-3">
      {!profileMode && (
        <div className="mb-3">
          <Link to={backToUsersPath} className="btn btn-sm btn-outline-secondary">
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Users
          </Link>
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '120px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : user ? (
        <>
          <div className="card mb-4">
            <div className="card-body">
            <div className="d-flex justify-content-between align-items-start flex-wrap">
              <div>
                <h3 className="mb-0">
                  {user.firstName} {user.lastName}
                </h3>
                <span className={`badge ${roleBadgeClass(user.role)} mt-2`}>
                  {user.role}
                </span>
                {resolveTester(user) !== 'none' && (
                  <span className={`badge text-lowercase ms-2 mt-2 ${testerBadgeClass(resolveTester(user))}`}>
                    {testerLabel(resolveTester(user))}
                  </span>
                )}
                <span
                  className="badge text-lowercase ms-2 mt-2"
                  style={loggingModeBadgeStyle(user.loggingMode ?? DEFAULT_LOGGING_MODE)}
                  title="Logging mode"
                >
                  {loggingModeShortLabel(user.loggingMode ?? DEFAULT_LOGGING_MODE)}
                </span>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                {canManageUser && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={handleDeleteUser}
                    disabled={deletingUser}
                  >
                    {deletingUser ? (
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                    ) : (
                      <FontAwesomeIcon icon={faTrash} className="me-1" />
                    )}
                    Delete User
                  </button>
                )}
              </div>
            </div>

            <hr />

            {actionMessage && (
              <div className="alert alert-success py-2">{actionMessage}</div>
            )}
            {actionError && (
              <div className="alert alert-danger py-2">{actionError}</div>
            )}

            <div className="row g-3">
              <div className="col-md-6">
                <div className="mb-3">
                  <div className="fw-bold text-muted small text-uppercase">User ID</div>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-break text-start"
                    onClick={() => userId && handleCopy(userId, 'id')}
                    title="Copy User ID"
                  >
                    <FontAwesomeIcon icon={faCopy} className="me-2" />
                    {userId}
                  </button>
                  {copiedField === 'id' && <span className="text-success ms-2 small">Copied!</span>}
                </div>
                <div className="mb-3">
                  <div className="fw-bold text-muted small text-uppercase">Email</div>
                  <button
                    type="button"
                    className="btn btn-link p-0 text-break text-start"
                    onClick={() => handleCopy(user.email, 'email')}
                    title="Copy email"
                  >
                    <FontAwesomeIcon icon={faCopy} className="me-2" />
                    {user.email}
                  </button>
                  {copiedField === 'email' && <span className="text-success ms-2 small">Copied!</span>}
                </div>
                <div>
                  <div className="fw-bold text-muted small text-uppercase">Last Login</div>
                  <div>
                    {user.lastLogin
                      ? new Date(user.lastLogin).toLocaleString()
                      : '—'}
                  </div>
                </div>
                {canManageUser && (
                  <div className="mt-3 me-5 border rounded p-3 bg-light">
                    <label htmlFor="new-password" className="fw-bold text-muted small text-uppercase d-block mb-2">
                      Password
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <FontAwesomeIcon icon={faKey} />
                      </span>
                      <input
                        id="new-password"
                        type="text"
                        className="form-control"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Generate or enter a new password"
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={generatePassword}
                        title="Generate random password"
                      >
                        <FontAwesomeIcon icon={faShuffle} className="me-1" />
                        Generate
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleCopy(newPassword, 'password')}
                        disabled={!newPassword}
                        title="Copy password"
                      >
                        <FontAwesomeIcon icon={faCopy} className="me-1" />
                        Copy
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handlePasswordUpdate}
                        disabled={updatingPassword || newPassword.length < 7}
                      >
                        {updatingPassword ? (
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        ) : (
                          <FontAwesomeIcon icon={faKey} className="me-1" />
                        )}
                        Set Password
                      </button>
                    </div>
                    <div className="form-text">
                      Passwords must be at least 7 characters.
                      {copiedField === 'password' && <span className="text-success ms-2">Copied!</span>}
                    </div>
                  </div>
                )}
              </div>
              {canManageUser && (
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="user-role" className="fw-bold text-muted small text-uppercase">User Type</label>
                    <select
                      id="user-role"
                      className="form-select"
                      value={user.role}
                      onChange={(e) => handleRoleChange(e.target.value as User['role'])}
                      disabled={updatingRole}
                    >
                      <option value="customer">Customer</option>
                      <option value="support">Support</option>
                      <option value="technician">Technician</option>
                      <option value="factory">Factory</option>
                      <option value="developer">Developer</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="user-tester" className="fw-bold text-muted small text-uppercase">Testing</label>
                    <select
                      id="user-tester"
                      className="form-select"
                      value={resolveTester(user)}
                      onChange={(e) => handleTesterChange(e.target.value as TesterLevel)}
                      disabled={updatingTester}
                    >
                      {TESTER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-3">
                    <label htmlFor="user-logging-mode" className="fw-bold text-muted small text-uppercase">
                      Logging Mode
                    </label>
                    <select
                      id="user-logging-mode"
                      className="form-select"
                      value={user.loggingMode ?? DEFAULT_LOGGING_MODE}
                      disabled={updatingLoggingMode}
                      onChange={(e) => handleLoggingModeChange(parseInt(e.target.value, 10))}
                    >
                      {LOGGING_MODE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Verbosity the mobile app uses when sending logs to Sentry.
                    </div>
                  </div>
                  {canToggleMqttRemoteControl && mqttRemoteControlToggle}
                </div>
              )}
              {!canManageUser && canToggleMqttRemoteControl && (
                <div className="col-md-6">
                  {mqttRemoteControlToggle}
                </div>
              )}
            </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <div>
                  <h5 className="mb-1">
                    <FontAwesomeIcon icon={faMobileScreenButton} className="me-2 text-primary" />
                    Mobile Devices
                  </h5>
                  <div className="text-muted small">
                    Phones this tester can use for internal testing.
                  </div>
                </div>
              </div>

              {(user.phoneDevices || []).length === 0 ? (
                <div className="text-muted mb-3">No phone devices added yet.</div>
              ) : (
                <div className="d-flex flex-column gap-2 mb-3">
                  {(user.phoneDevices || []).map((phoneDevice) => (
                    <div
                      key={phoneDevice._id || `${phoneDevice.platform}-${phoneDevice.name}`}
                      className="d-flex align-items-center justify-content-between border rounded p-2"
                    >
                      <div>
                        <div className="fw-bold">{phoneDevice.name}</div>
                        <span className="badge text-bg-light text-uppercase">{phoneDevice.platform}</span>
                        <div className="small text-muted mt-1">
                          Device Id {phoneDevice.appDeviceId || '—'}
                        </div>
                      </div>
                      {(canManageUser || profileMode) && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemovePhoneDevice(phoneDevice._id)}
                          disabled={updatingPhoneDevices}
                        >
                          <FontAwesomeIcon icon={faTrash} className="me-1" />
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {(canManageUser || profileMode) && (
                <div className="row g-2 align-items-end">
                  <div className="col-md-6">
                    <label htmlFor="phone-device-name" className="form-label fw-bold">Device Name</label>
                    <input
                      id="phone-device-name"
                      className="form-control"
                      value={phoneDeviceName}
                      onChange={(event) => setPhoneDeviceName(event.target.value)}
                      placeholder="iPhone 16 Pro"
                      disabled={updatingPhoneDevices}
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="phone-device-platform" className="form-label fw-bold">Platform</label>
                    <select
                      id="phone-device-platform"
                      className="form-select"
                      value={phoneDevicePlatform}
                      onChange={(event) => setPhoneDevicePlatform(event.target.value as PhoneDevice['platform'])}
                      disabled={updatingPhoneDevices}
                    >
                      <option value="ios">iOS</option>
                      <option value="android">Android</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <button
                      type="button"
                      className="btn btn-primary w-100"
                      onClick={handleAddPhoneDevice}
                      disabled={updatingPhoneDevices || !phoneDeviceName.trim()}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" />
                      Add Device
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}

      <div className="card mb-4">
        <div className="card-body">
          <h5 className="mb-3">
            <FontAwesomeIcon icon={faMicrochip} className="me-2 text-primary" />
            Assigned Devices
          </h5>
          <DeviceTable
            devices={devices}
            loading={devicesLoading}
            error={devicesError}
            onToggleMarked={handleToggleMarked}
            onUpdateLoggingMode={handleUpdateLoggingMode}
            onFirmwareInstall={canInstallFirmware ? setFirmwareUpdateDevice : undefined}
            onAlertsOpen={setAlertDevice}
            updatingOTA={updatingOTA}
          />
        </div>
      </div>

      {!profileMode && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <div>
                <h5 className="mb-1">
                  <FontAwesomeIcon icon={faClipboardList} className="me-2 text-primary" />
                  Survey Results
                </h5>
                <div className="text-muted small">In-app survey responses submitted by this user.</div>
              </div>
            </div>

            {surveyResultsLoading ? (
              <div className="d-flex justify-content-center py-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading survey results...</span>
                </div>
              </div>
            ) : surveyResultsError ? (
              <div className="alert alert-danger py-2 mb-0">{surveyResultsError}</div>
            ) : surveyResults.length === 0 ? (
              <div className="text-muted">No survey results yet.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Survey</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th>Device</th>
                      <th>Answers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {surveyResults.map((result) => (
                      <tr key={result._id}>
                        <td className="fw-semibold">{result.survey?.name || 'Unknown survey'}</td>
                        <td>
                          <span className={`badge ${result.status === 'opted_out' ? 'text-bg-secondary' : 'text-bg-success'}`}>
                            {surveyResultStatusLabel(result.status)}
                          </span>
                        </td>
                        <td>{formatSurveyResultDate(result.completedAt || result.createdAt)}</td>
                        <td className="text-muted">{result.deviceId || '—'}</td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            {surveyAnswerLines(result).map((line, index) => (
                              <span key={`${result._id}-answer-${index}`} className="small">
                                {line}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <FirmwareUpdateModal
        apiBaseUrl={API_BASE_URL}
        device={firmwareUpdateDevice}
        isOpen={firmwareUpdateDevice !== null}
        onClose={() => setFirmwareUpdateDevice(null)}
      />
      <DeviceAlertModal
        apiBaseUrl={API_BASE_URL}
        device={alertDevice}
        isOpen={alertDevice !== null}
        onClose={() => setAlertDevice(null)}
      />

      {deviceLoggingPrompt !== null && (
        <>
          <div className="modal-backdrop fade show" />
          <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1} role="dialog">
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Update devices too?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setDeviceLoggingPrompt(null)}
                    disabled={applyingDeviceLogging}
                  />
                </div>
                <div className="modal-body">
                  Would you also like to update this user's{' '}
                  <strong>{devices.length} device{devices.length === 1 ? '' : 's'}</strong> to:
                  <div className="mt-2">
                    <span
                      className="badge rounded-pill fs-6 px-3 py-2"
                      style={loggingModeBadgeStyle(deviceLoggingPrompt)}
                    >
                      {loggingModeLabel(deviceLoggingPrompt)}
                    </span>
                  </div>
                  <div className="form-check mt-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="sendLoggingCommand"
                      checked={sendLoggingCommand}
                      onChange={(e) => setSendLoggingCommand(e.target.checked)}
                      disabled={applyingDeviceLogging}
                    />
                    <label className="form-check-label" htmlFor="sendLoggingCommand">
                      Update logging mode immediately
                    </label>
                    <div className="form-text">
                      Sends an MQTT command to each online device so the change takes effect right away.
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setDeviceLoggingPrompt(null)}
                    disabled={applyingDeviceLogging}
                  >
                    No
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => applyLoggingModeToDevices(deviceLoggingPrompt)}
                    disabled={applyingDeviceLogging}
                  >
                    {applyingDeviceLogging ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Updating...
                      </>
                    ) : (
                      'Yes, update devices'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserDetail;
